<template>
  <PageSection font="medium">
    <SectionGroup gap="medium">
      <SectionBox>
        <BasicSection appearance="heading">
          <PageTitle title="Специалисты Хабр Q&A" />
        </BasicSection>

        <BasicTabs
          v-if="tabs"
          :tabs="tabs"
          appearance="primary"
        />

        <BasicSection appearance="search-panel">
          <SearchPanel
            :indicator-visible="hasCustomizedFilters"
            @openFilters="openModalFilters"
          />

          <TransitionExpand v-if="!mediaQueries.mobile">
            <SearchFilters v-if="hasCustomizedFilters" :key="hasCustomizedFilters">
              <SectionGroup gap="medium">
                <SelectedFilters
                  :items="filters.selectedList"
                  title="Выбранные вами фильтры:"
                  :max="2"
                  @remove="filters.resetOne($event), scheduleListUpdate()"
                />

                <FiltersControls
                  appearance="search"
                  @reset="filters.reset(), scheduleListUpdate()"
                />
              </SectionGroup>
            </SearchFilters>
          </TransitionExpand>
          
          <TransitionExpand>
            <SearchTotal
              v-if="currentResumes.list.length !== 0"
              appearance="search-panel"
              v-model="searchResultsTitle"
            />
          </TransitionExpand>
        </BasicSection>
      </SectionBox>
  
      <ResourceAction
        ref="modalFiltersAction"
        :action="action"
        :debounce="400"
        @success="onModalRequestSuccess"
        v-slot="{ isLoading: isModalLoading }"
      >
        <PaginatedResource
          ref="listResource"
          v-model="currentResumes"
          :action="action"
          :params="params"
          :debounce="400"
          @success="onListRequestSuccess"
          v-slot="{ isLoading }"
        >
          <TransitionExpand>
            <WithPagination
              :params="mappedParams"
              :key="isLoading"
              ref="scrollTarget"
              @click="onPagination"
            >
              <SectionGroup gap="medium">
                <template v-if="isLoading || isModalLoading">
                  <SectionBox
                    v-for="(_, index) in Array(currentResumes.list.length < 3 ? 3 : currentResumes.list.length)"
                    :key="index"
                  >
                    <BasicSection appearance="horizontal-card">
                      <ResumeCardSkeleton />
                    </BasicSection>
                  </SectionBox>
                </template>
      
                <template v-if="!isLoading && !isModalLoading">
                  <template v-if="currentResumes.list.length !== 0">
                    <SectionBox
                      v-for="resume in currentResumes.list"
                      :key="`resume-${resume.id}`"
                    >
                      <BasicSection appearance="horizontal-card">
                        <ExternalResumeCard :resume="resume" type="qa"/>
                      </BasicSection>
                    </SectionBox>
                  </template>
        
                  <SectionBox v-else-if="currentResumes.meta.totalResults === 0">
                    <BasicSection>
                      <NoContent
                        :title="$options.noContent.title"
                        :description="$options.noContent.description"
                        :icon="$options.noContent.icon"
                        :actions="$options.noContent.actions"
                      />
                    </BasicSection>
                  </SectionBox>
        
                  <SectionBox
                    v-if="currentResumes.limitedAccess"
                  >
                    <LimitedAccessCard
                      :limited-access="currentResumes.limitedAccess"
                    />
                  </SectionBox>
                </template>
              </SectionGroup>
            </WithPagination>
          </TransitionExpand>
        </PaginatedResource>
      </ResourceAction>
    </SectionGroup>

    <VuePortal target="[data-list-sidebar]">
      <ListPageSidebar v-if="!mediaQueries.mobile">
        <template #filters>
          <QAResumeFilters
            :filters="filters.items"
            :dynamic-options="filters.dynamicOptions"
            @change="scheduleListUpdate"
          >
            <template #controls>
              <FiltersControls
                v-if="hasCustomizedFilters"
                @reset="filters.reset(), scheduleListUpdate()"
              />
            </template>
          </QAResumeFilters>
        </template>
      </ListPageSidebar>
    </VuePortal>

    <TransitionFade body-scroll-lock>
      <FiltersModal
        v-if="filtersModalVisible"
        @close="closeModalFilters"
      >
        <template #filters="{ filters: items, dynamicOptions }">
          <QAResumeFilters
            :filters="items"
            :dynamic-options="dynamicOptions"
            @change="onModalFiltersUpdate"
          />
        </template>

        <template #submit-button="{ filters, dynamicOptions }">
          <BasicButton
            @click="applyModalFilters({ filters, dynamicOptions })"
            appearance="primary"
            full-width
            size="xl"
          >{{applyFiltersTitle}}</BasicButton>
        </template>
      </FiltersModal>
    </TransitionFade>
  </PageSection>
</template>

<script>
  import { QAResumesService } from 'src/services/qa-resumes.js';
  import { getFormattedNumber, getRussianPluralDeclension } from 'src/utils/translation.js';

  import BasicButton from 'src/components/BasicButton/BasicButton.vue';
  import FiltersControls from 'src/components/filters/FiltersControls/FiltersControls.vue';
  import FiltersModal from 'src/components/filters/FiltersModal/FiltersModal.vue';
  import SearchFilters from 'src/components/filters/SearchFilters/SearchFilters.vue';
  import SelectedFilters from 'src/components/filters/SelectedFilters/SelectedFilters.vue';
  import ListPageSidebar from 'src/components/ListPageSidebar/ListPageSidebar.vue';
  import PageTitle from 'src/components/PageTitle/PageTitle.vue';
  import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
  import WithPagination from 'src/components/pagination/WithPagination/WithPagination.vue';
  import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
  import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
  import ExternalResumeCard from 'src/components/resumes/ExternalResumeCard/ExternalResumeCard.vue';
  import LimitedAccessCard from 'src/components/resumes/LimitedAccessCard/LimitedAccessCard.vue';
  import QAResumeFilters from 'src/components/resumes/QAResumeFilters/QAResumeFilters.vue';
  import ResumeCardSkeleton from 'src/components/resumes/ResumeCardSkeleton/ResumeCardSkeleton.vue';
  import SearchPanel from 'src/components/search/SearchPanel/SearchPanel.vue';
  import SearchTotal from 'src/components/SearchTotal/SearchTotal.vue';
  import SectionBox from 'src/components/SectionBox/SectionBox.vue';
  import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
  import PageSection from 'src/components/sections/PageSection/PageSection.vue';
  import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
  import BasicTabs from 'src/components/tabs/BasicTabs/BasicTabs.vue';
  import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
  import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';
  import VuePortal from 'src/components/VuePortal/VuePortal.vue';

  import noContentIconSrc from 'images/no_content.icon.search.png';

  const resumesDeclension = getRussianPluralDeclension(
    'Найден ${0} специалист',
    'Найдено ${0} специалиста',
    'Найдено ${0} специалистов'
  );

  const submitButtonDeclension = getRussianPluralDeclension(
    'Показать ${0} специалиста',
    'Показать ${0} специалиста',
    'Показать ${0} специалистов'
  );

  export default {
    name: 'QAResumeListPage',
    components: {
      ResumeCardSkeleton,
      QAResumeFilters,
      ExternalResumeCard,
      NoContent,
      LimitedAccessCard,
      FiltersModal,
      TransitionFade,
      ResourceAction,
      ListPageSidebar,
      VuePortal,
      WithPagination,
      SearchTotal,
      PaginatedResource,
      FiltersControls,
      SelectedFilters,
      SectionGroup,
      SearchFilters,
      TransitionExpand,
      SearchPanel,
      BasicTabs,
      BasicButton,
      PageTitle,
      BasicSection,
      SectionBox,
      PageSection,
    },
    noContent: {
      title: 'Специалисты не найдены',
      description: 'Не удалось найти специалистов по заданным параметрам. Попробуйте изменить условия поиска.',
      icon: {
        src: noContentIconSrc,
        width: '77px',
        height: '77px',
      },
    },
    inject: [
      'mediaQueries',
      'visitor',
      'filters',
    ],
    props: {
      tabs: Array,
      resumes: Object,
    },
    data() {
      return {
        currentResumes: this.resumes,
        modalResumes: null,
        filtersModalVisible: false,
      }
    },
    methods: {
      action: QAResumesService.getList,
      scheduleListUpdate() {
        this.$refs.listResource.loadAndReplace(this.params);
      },
      onListRequestSuccess({ params }) {
        QAResumesService.syncLocation(params);
      },
      onPagination() {
        this.$refs.scrollTarget.$el.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'auto'
        });
      },
      openModalFilters() {
        this.filtersModalVisible = true;
        this.modalResumes = this.currentResumes;
      },
      closeModalFilters() {
        this.$refs.modalFiltersAction.cancel();
        this.filtersModalVisible = false;
      },
      onModalFiltersUpdate(filters) {
        this.$refs.modalFiltersAction.dispatch({ ...filters });
      },
      onModalRequestSuccess({ data }) {
        if (this.filtersModalVisible) {
          this.modalResumes = data;
        } else {
          this.currentResumes = data;
        }
      },
      applyModalFilters({ filters, dynamicOptions }) {
        this.filtersModalVisible = false;
        this.filters.set({ filters, dynamicOptions });
        this.currentResumes = this.modalResumes;
        const params = { ...filters };
        QAResumesService.syncLocation(params);
      },
    },
    computed: {
      params() {
        return { ...this.filters.items };
      },
      searchResultsTitle() {
        const value = getFormattedNumber(this.currentResumes.meta.totalResults);
        return resumesDeclension(value).replace('${0}', value);
      },
      hasCustomizedFilters() {
        return this.filters.selectedList.length !== 0;
      },
      applyFiltersTitle() {
        if (!this.modalResumes) return null;
        const value = this.modalResumes.meta.totalResults;
        return submitButtonDeclension(value).replace('${0}', value);
      },
      mappedParams() {
        return QAResumesService.getMappedParams(this.params);
      }
    },
  }
</script>